import React from 'react';
import { Button, Form, Input, Modal, Upload, UploadFile, Typography, Collapse } from 'antd';
import { VideoCameraOutlined, PlaySquareOutlined, FileImageOutlined, PictureOutlined } from '@ant-design/icons';
import JSZip from 'jszip';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { ResponsiveInputNumber } from '../ui/inputs/responsive-input-number';
import { useCreateHtmlBanner } from '../../hooks/banners/creation/api/use-create-html-banner';
import { useBanners } from '../../context/banners';

// import { tokens } from '../../theme/tokens';
// import { CreativityContentData } from '../../types/banner';
// import { useModal } from '../../context/modal';

const { Text, Title } = Typography;

interface HTMLExportParams {
  width: number;
  height: number;
  title: string;
  mediaSrc: string;
  clickUrl: string;
  exitId: string;
  media: UploadFile;
  typeOfMedia: 'video' | 'image';
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const buildExport = async ({
  width,
  height,
  title,
  mediaSrc,
  clickUrl,
  exitId,
  media,
  typeOfMedia,
  utm_source,
  utm_medium,
  utm_campaign,
  utm_term,
  utm_content
}: HTMLExportParams) => {
  const zip = new JSZip();

  const buildClickUrlWithUTM = () => {
    const url = new URL(clickUrl);
    if (utm_source) url.searchParams.append('utm_source', utm_source);
    if (utm_medium) url.searchParams.append('utm_medium', utm_medium);
    if (utm_campaign) url.searchParams.append('utm_campaign', utm_campaign);
    if (utm_term) url.searchParams.append('utm_term', utm_term);
    if (utm_content) url.searchParams.append('utm_content', utm_content);
    return url.toString();
  };

  const finalClickUrl = buildClickUrlWithUTM();

  const isVideo = typeOfMedia === 'video';
  const mediaMarkup = isVideo
    ? `<video id="banner-media" class="polite-load" autoplay loop muted playsinline webkit-playsinline><source src="${mediaSrc}" type="${media.type || 'video/mp4'}"></video>`
    : `<img id="banner-media" class="polite-load" src="${mediaSrc}" alt="${title}">`;

  const mediaPlaybackJs = isVideo
    ? `media.style.display="block"; media.play().catch(function (e){ console.log("Autoplay:", e);});`
    : `media.style.display="block";`;

  const mediaEventListenersJs = isVideo
    ? `media.addEventListener("play", function (){ console.log("Event: Video started");}); media.addEventListener("ended", function (){ console.log("Event: Video ended");});`
    : '';

  const clickTagScript = `<script>var clickTag = "${finalClickUrl}";</script>`;

  const html5Template = `<html lang="es"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><meta name="ad.size" content="width=${width},height=${height}"><script src="https://s0.2mdn.net/ads/studio/Enabler.js"></script>${clickTagScript}<title>${title}</title><style>*{ margin: 0; padding: 0; box-sizing: border-box;} html, body{ width: 100%; height: 100%; overflow: hidden;} #banner-container{ position: relative; width:${width}px; height:${height}px; overflow: hidden; background: #000;} #banner-media{ position: absolute; width: 100%; height: 100%; object-fit: cover;} #click-area{ position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer; z-index: 10; background: transparent;} #click-area:hover{ background: rgba(255, 255, 255, 0.05);} .polite-load{ display: none;} </style></head><body><div id="banner-container">${mediaMarkup}<div id="click-area"></div></div><script>(function (){ var clickArea=document.getElementById("click-area"); var media=document.getElementById("banner-media"); function exitHandler(){ if (window.Enabler && Enabler.isInitialized()){ Enabler.exit("${exitId}", clickTag);} else{ window.open(clickTag, "_blank");}} if (window.Enabler){ Enabler.addEventListener(studio.events.StudioEvent.INIT, function (){ clickArea.addEventListener("click", exitHandler);}); Enabler.addEventListener(studio.events.StudioEvent.PAGE_LOADED, function (){ media.classList.remove("polite-load"); ${mediaPlaybackJs}});} else{ clickArea.addEventListener("click", exitHandler); document.addEventListener("DOMContentLoaded", function (){ setTimeout(function (){ media.classList.remove("polite-load"); ${mediaPlaybackJs}}, 100);});} ${mediaEventListenersJs} clickArea.addEventListener("click", function (){ console.log("Event: CreativityContentData clicked");});})(); </script></body></html>`;

  const enablerScript = `"undefined"==typeof Enabler&&(window.Enabler={addEventListener:function(n,e){document.addEventListener(n,e)},exit:function(n,e){window.open(e,"_blank")},isInitialized:function(){return!0}},window.studio={events:{StudioEvent:{INIT:"enabler_init",PAGE_LOADED:"page_loaded"}}},setTimeout((function(){var n=new Event("enabler_init");document.dispatchEvent(n);var e=new Event("page_loaded");document.dispatchEvent(e)}),100));`;
  const manifest = JSON.stringify(
    {
      name: title,
      version: '1.0.0',
      description: `HTML5 ${typeOfMedia.charAt(0).toUpperCase() + typeOfMedia.slice(1)} Creativity Content - Generated with DigitalTrendingLab`,
      width,
      height,
      clickTag: finalClickUrl,
      utm: {
        utm_source: utm_source || '',
        utm_medium: utm_medium || '',
        utm_campaign: utm_campaign || '',
        utm_term: utm_term || '',
        utm_content: utm_content || ''
      },
      exitId,
      platform: 'google',
      features: {
        politeLoading: true,
        backupImage: true,
        eventTracking: true
      },
      files: ['index.html', 'enabler.js', 'manifest.json', mediaSrc],
      created: new Date().toISOString(),
      generator: 'DigitalTrendingLab'
    },
    null,
    2
  );

  zip.file('index.html', html5Template);
  zip.file('enabler.js', enablerScript);
  zip.file('manifest.json', manifest);

  const mediaBase64 = await fileToBase64(media.originFileObj as File);

  zip.file(mediaSrc, mediaBase64.split(',')[1], { base64: true });

  const content = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 }
  });

  downloadBlob(content, `${title.replace(/\s+/g, '_')}-bundle.zip`);
};

export const BannerExport: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { spacing, fontSize, screens } = useResponsiveSizes();
  const { refreshAllBanners } = useBanners();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { createHtmlBanner, loading: uploading } = useCreateHtmlBanner();

  const [loading, setLoading] = React.useState(false);
  const [form] = Form.useForm<{
    name: string;
    width: number;
    height: number;
    clickUrl: string;
    exitId: string;
    bannerFile: UploadFile[];
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  }>();

  const bannerFile = Form.useWatch('bannerFile', form);
  const [mediaUrl, setMediaUrl] = React.useState<string>();
  const [typeOfMedia, setTypeOfMedia] = React.useState<'video' | 'image'>('video');
  const [isDraggingFile, setIsDraggingFile] = React.useState(false);

  React.useEffect(() => {
    if (bannerFile && bannerFile.length > 0) {
      const file = bannerFile[0];
      if (file) {
        const filename = file.name;

        form.setFieldValue('name', form.getFieldValue('name') || filename.replace(/\.[^/.]+$/, ''));

        const url = URL.createObjectURL(file.originFileObj as Blob);
        setMediaUrl(url);
        setTypeOfMedia(file.type && file.type.startsWith('video/') ? 'video' : 'image');

        return () => {
          URL.revokeObjectURL(url);
        };
      }
    }
  }, [bannerFile, form]);

  const onMetaDataLoaded = React.useCallback(() => {
    if (videoRef.current) {
      form.setFieldsValue({
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight
      });
    }
  }, [form]);

  const onImageLoaded = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      form.setFieldsValue({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    },
    [form]
  );

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const onFormFinish = async (values: {
    name: string;
    width: number;
    height: number;
    clickUrl: string;
    exitId: string;
    bannerFile: UploadFile[];
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  }) => {
    setLoading(true);

    if (values.bannerFile && values.bannerFile.length > 0) {
      const file = values.bannerFile[0];

      if (file) {
        await createHtmlBanner({
          title: values.name,
          media: file.originFileObj as File,
          width: values.width,
          height: values.height,
          clickUrl: values.clickUrl,
          exitId: values.exitId,
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          utm_term: values.utm_term,
          utm_content: values.utm_content
        });

        // Descargar ZIP local
        await buildExport({
          title: values.name,
          width: values.width,
          height: values.height,
          clickUrl: values.clickUrl,
          exitId: values.exitId,
          mediaSrc: file.name,
          media: file,
          typeOfMedia,
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          utm_term: values.utm_term,
          utm_content: values.utm_content
        });
      }
    }

    setLoading(false);
    form.resetFields();
    setMediaUrl(undefined);
    await refreshAllBanners();
    onClose();
  };

  React.useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      // Solo activar si se está arrastrando un archivo
      if (e.dataTransfer?.types && e.dataTransfer.types.includes('Files')) {
        setIsDraggingFile(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      // Solo desactivar si el cursor sale de la ventana
      if (e.relatedTarget === null) {
        setIsDraggingFile(false);
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDraggingFile(false);
    };

    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, []);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={screens.lg ? 900 : '90%'}
      centered
      style={{ borderRadius: 16 }}
      styles={{
        body: {
          padding: 0,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: '90vh',
          overflow: 'hidden'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)'
        },
        content: {
          padding: 0,
          borderRadius: 20,
          overflow: 'hidden'
        }
      }}
      closeIcon={null}
    >
      <div
        style={{
          padding: spacing.md,
          background: 'white',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileImageOutlined style={{ fontSize: 24, color: 'white' }} />
          </div>

          <Title
            level={3}
            style={{
              margin: 0,
              color: '#0f172a',
              fontWeight: 700,
              fontSize: fontSize.xxl
            }}
          >
            HTML5 Creativity Content Export
          </Title>
        </div>
      </div>
      <div
        style={{
          padding: spacing.md,
          background: 'white',
          maxHeight: '70vh',
          overflow: 'auto',
          scrollbarColor: '#e3e3e3 #FFFFFF'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: screens.md ? '1fr 1fr' : '1fr',
            gap: spacing.xl,
            height: '100%'
          }}
        >
          {/* Formulario */}
          <div
            style={{
              overflowY: screens.md ? 'auto' : undefined,
              maxHeight: screens.md ? '70vh' : undefined,
              paddingRight: spacing.md,
              scrollbarColor: '#e3e3e3 #FFFFFF'
            }}
          >
            <Form form={form} layout='vertical' initialValues={{ exitId: 'clickTag' }} onFinish={onFormFinish}>
              <Form.Item
                label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>Título</Text>}
                name='name'
                rules={[{ required: true, message: 'El título es requerido' }]}
              >
                <Input placeholder='Nombre del banner' style={{ fontSize: fontSize.base }} />
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
                <Form.Item
                  label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>Ancho</Text>}
                  name='width'
                  rules={[{ type: 'number', required: true }]}
                >
                  <ResponsiveInputNumber placeholder='Ancho del banner' suffix='px' />
                </Form.Item>

                <Form.Item
                  label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>Alto</Text>}
                  name='height'
                  rules={[{ type: 'number', required: true }]}
                >
                  <ResponsiveInputNumber placeholder='Altura del banner' suffix='px' />
                </Form.Item>
              </div>

              <Form.Item
                label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>URL de destino</Text>}
                name='clickUrl'
                rules={[{ type: 'url', required: true, message: 'Ingresa una URL válida' }]}
              >
                <Input placeholder='URL de clic' style={{ fontSize: fontSize.base }} />
              </Form.Item>

              <Form.Item
                label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>Exit ID</Text>}
                name='exitId'
                rules={[{ required: true }]}
              >
                <Input placeholder='ID de salida' style={{ fontSize: fontSize.base }} />
              </Form.Item>

              <Collapse
                ghost
                style={{ marginTop: spacing.md }}
                items={[
                  {
                    key: 'utm',
                    styles: { body: { padding: 0 }, header: { padding: `0 0 ${spacing.md}px 0` } },
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        <div
                          style={{
                            width: 4,
                            height: 20,
                            backgroundColor: '#667eea',
                            borderRadius: 2
                          }}
                        />
                        <Text style={{ fontSize: fontSize.base }}>Parámetros UTM (Opcional)</Text>
                      </div>
                    ),
                    children: (
                      <>
                        <Form.Item
                          label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>UTM Source</Text>}
                          name='utm_source'
                        >
                          <Input placeholder='Ej: google, facebook' style={{ fontSize: fontSize.base }} />
                        </Form.Item>

                        <Form.Item
                          label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>UTM Medium</Text>}
                          name='utm_medium'
                        >
                          <Input placeholder='Ej: cpc, display' style={{ fontSize: fontSize.base }} />
                        </Form.Item>

                        <Form.Item
                          label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>UTM Campaign</Text>}
                          name='utm_campaign'
                        >
                          <Input placeholder='Ej: summer_sale' style={{ fontSize: fontSize.base }} />
                        </Form.Item>

                        <Form.Item
                          label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>UTM Term</Text>}
                          name='utm_term'
                        >
                          <Input placeholder='Ej: running_shoes' style={{ fontSize: fontSize.base }} />
                        </Form.Item>

                        <Form.Item
                          label={<Text style={{ fontSize: fontSize.base, fontWeight: 500 }}>UTM Content</Text>}
                          name='utm_content'
                        >
                          <Input placeholder='Ej: asset_1' style={{ fontSize: fontSize.base }} />
                        </Form.Item>
                      </>
                    )
                  }
                ]}
              />

              <Form.Item
                name='bannerFile'
                valuePropName='fileList'
                getValueFromEvent={normFile}
                rules={[{ required: true, message: 'Sube un video o imagen' }]}
              >
                <Upload.Dragger
                  rootClassName='html5-media-dragger'
                  name='files'
                  multiple={false}
                  accept='video/mp4,image/jpeg,image/png'
                  beforeUpload={() => false}
                  style={{
                    background: '#f8fafc',
                    border: isDraggingFile ? '2px dashed #667eea' : '2px dashed #cbd5e1',
                    borderRadius: '12px',
                    boxShadow: isDraggingFile
                      ? '0 0 20px rgba(102, 126, 234, 0.4), 0 0 20px rgba(102, 126, 234, 0.2), inset 0 0 20px rgba(102, 126, 234, 0.1)'
                      : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <p className='ant-upload-drag-icon'>
                    {bannerFile && bannerFile.length > 0 && typeOfMedia === 'image' ? (
                      <PictureOutlined style={{ fontSize: 48, color: '#667eea' }} />
                    ) : (
                      <VideoCameraOutlined style={{ fontSize: 48, color: '#667eea' }} />
                    )}
                  </p>
                  <p
                    style={{ fontSize: fontSize.base, fontWeight: 600, margin: `${spacing.sm}px 0` }}
                    className='drag-text'
                  >
                    Arrastra tu archivo acá
                  </p>
                  <p className='drag-subtext' style={{ fontSize: fontSize.sm, color: '#64748b' }}>
                    o hace click para seleccionar
                  </p>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item style={{ marginTop: spacing.lg, marginBottom: 0 }}>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading || uploading}
                  block
                  size='large'
                  style={{
                    height: '48px',
                    fontSize: fontSize.base,
                    fontWeight: 600,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  {loading || uploading ? 'Generando...' : 'Exportar'}
                </Button>
              </Form.Item>
            </Form>
          </div>

          {/* Preview */}
          <div
            style={{
              background: '#f8fafc',
              borderRadius: '12px',
              padding: spacing.lg,
              border: '1px solid #e2e8f0',
              minHeight: '100px',
              maxHeight: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {mediaUrl && typeOfMedia === 'video' ? (
              <video
                ref={videoRef}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  borderRadius: '8px',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                }}
                controls
                src={mediaUrl}
                onLoadedMetadata={onMetaDataLoaded}
                onClick={() => {
                  const clickUrl = form.getFieldValue('clickUrl');
                  if (clickUrl) {
                    window.open(clickUrl, '_blank');
                  }
                }}
              />
            ) : mediaUrl && typeOfMedia === 'image' ? (
              <img
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  borderRadius: '8px',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  objectFit: 'contain'
                }}
                src={mediaUrl}
                alt='Preview'
                onLoad={onImageLoaded}
                onClick={() => {
                  const clickUrl = form.getFieldValue('clickUrl');
                  if (clickUrl) {
                    window.open(clickUrl, '_blank');
                  }
                }}
              />
            ) : (
              <div style={{ textAlign: 'center' }}>
                <PlaySquareOutlined style={{ fontSize: 64, color: '#cbd5e1', marginBottom: spacing.md }} />
                <Text style={{ fontSize: fontSize.base, color: '#64748b', display: 'block' }}>Vista previa</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
